import {
    AccountConnection,
    Badge,
    BlockStack,
    Button,
    InlineStack,
    Text,
} from "@shopify/polaris";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import type { AtdDiagnostics } from "../../app/models/Atd";

const getConnectionDetails = (loading: boolean, diagnostics?: AtdDiagnostics, error?: string) => {
    if (loading) {
        return "Checking ATD connection status.";
    }

    if (error) {
        return error;
    }

    if (!diagnostics?.profileConfigured) {
        return "No ATD account connected.";
    }

    const location = diagnostics.locationNumber ? `Location ${diagnostics.locationNumber}` : "ATD account connected";
    return diagnostics.profileActive ? location : `${location}. Sync is disabled.`;
};

const getStatusBadge = (loading: boolean, diagnostics?: AtdDiagnostics, error?: string) => {
    if (loading) {
        return <Badge tone="info">Loading</Badge>;
    }

    if (error) {
        return <Badge tone="critical">Unable to load</Badge>;
    }

    if (!diagnostics?.profileConfigured) {
        return null;
    }

    return (
        <Badge tone={diagnostics.profileActive ? "success" : "attention"}>
            {diagnostics.profileActive ? "Active" : "Inactive"}
        </Badge>
    );
};

const AtdIntegrationSettings: React.FC = () => {
    const navigate = useNavigate();
    const [diagnostics, setDiagnostics] = useState<AtdDiagnostics>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const accountConnected = Boolean(diagnostics?.profileConfigured && !error);

    useEffect(() => {
        let mounted = true;

        agent.Atd.diagnostics()
            .then((result) => {
                if (!mounted) return;

                setDiagnostics(result);
                setError(undefined);
            })
            .catch(() => {
                if (!mounted) return;

                setDiagnostics(undefined);
                setError("Unable to load ATD diagnostics.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    return (
        <AccountConnection
            accountName={diagnostics?.locationNumber ? `Location ${diagnostics.locationNumber}` : ""}
            connected={accountConnected}
            title="American Tire Distributors"
            details={getConnectionDetails(loading, diagnostics, error)}
            action={{
                content: loading ? "Checking..." : diagnostics?.profileConfigured ? "Manage" : "Connect",
                onAction: () => {
                    if (!loading) navigate("/integrations/atd/profile");
                },
            }}
            termsOfService={(
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                        <Text as="p" variant="bodyMd" tone="subdued">
                            Product catalog, inventory, fitment data, ship-to-home fulfillment, and fulfillment status tracking.
                        </Text>
                        {getStatusBadge(loading, diagnostics, error)}
                    </InlineStack>
                    {accountConnected && (
                        <InlineStack gap="200">
                            <Button onClick={() => navigate("/integrations/atd/orders")} disabled={loading}>Open orders</Button>
                            <Button onClick={() => navigate("/integrations/atd/preview")} disabled={loading}>Preview order</Button>
                        </InlineStack>
                    )}
                </BlockStack>
            )}
        />
    );
};

export default AtdIntegrationSettings;
